import { ChangeBadge } from '@bcecloud/portal-ui';

/**
 * How the page and page-type tables show change against the previous window:
 * a percent for counts, places for search position, and when a page was
 * first recorded.
 */

/** Blank-value marker, matching the tables' other empty cells. */
const NONE = '—';

/** A count's change as a colored percent, or a dash with no baseline. */
export function PercentChange({ value }: { readonly value: number | null }): React.ReactElement {
  return value === null ? <span className="text-portal-muted">{NONE}</span> : <ChangeBadge value={value} />;
}

/**
 * How a search position moved, current − previous. The arrow follows the
 * number and the color follows the meaning: lower is better, so 21 → 15 is a
 * green down arrow and 15 → 21 a red up arrow. A dash without both windows.
 */
export function PositionChange({
  value,
  previous,
  className,
}: {
  readonly value: number | null;
  readonly previous?: number | null;
  readonly className?: string;
}): React.ReactElement {
  if (value === null) return <span className="text-portal-muted">{NONE}</span>;
  return (
    <ChangeBadge
      value={value}
      suffix=""
      downIsGood
      {...(className ? { className } : {})}
      {...(previous !== undefined && previous !== null ? { title: `was ${previous.toFixed(1)}` } : {})}
    />
  );
}

/**
 * How a search position moved: current − previous, to one decimal. Negative
 * is an improvement. Show it with `downIsGood`. Null unless both windows have one.
 */
export function positionDelta(current: number | null, previous: number | null): number | null {
  return current === null || previous === null ? null : Math.round((current - previous) * 10) / 10;
}

/**
 * A position tile's hint, in words: whether the number rose or fell, and
 * whether that's good ("Improved from 12.1 (lower is better)").
 */
export function positionHint(current: number | null, previous: number | null): string {
  if (previous === null) return 'Lower is better';
  const change = positionDelta(current, previous);
  const verdict = change === null || change === 0 ? 'Level with' : change < 0 ? 'Improved from' : 'Slipped from';
  return `${verdict} ${previous.toFixed(1)} (lower is better)`;
}

/** " place" or " places" after a change, for a stat tile's `changeSuffix`. */
export function placesSuffix(change: number | null): string {
  return change !== null && Math.abs(change) === 1 ? ' place' : ' places';
}

/** A search position to one decimal, or a dash. */
export function positionLabel(position: number | null): string {
  return position === null ? NONE : position.toFixed(1);
}

const SHORT_DATE = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
const MONTH_YEAR = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });

/**
 * When a page was first recorded ("Sep 15, 2026"). A page already there when
 * our records begin reads "Jun 2023 or earlier".
 */
export function firstSeenLabel(firstSeen: string | null, atHistoryStart: boolean): string {
  if (firstSeen === null) return NONE;
  const day = new Date(`${firstSeen}T00:00:00Z`);
  return atHistoryStart ? `${MONTH_YEAR.format(day)} or earlier` : SHORT_DATE.format(day);
}

/** When a page was last recorded ("Oct 5, 2026"), or a dash. */
export function lastSeenLabel(lastSeen: string | null): string {
  return lastSeen === null ? NONE : SHORT_DATE.format(new Date(`${lastSeen}T00:00:00Z`));
}
